import {
  SearchCommand,
  CreateCommand,
  DeleteCommand,
  UpdateCommandDescription,
  UpdateCommandName,
  Command,
  CreateCommandInput,
  UpdateCommandDescriptionInput,
  UpdateCommandNameInput,
  GetCommand,
  UpdateCommandState
} from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Commands/PathItems";
import { SearchInput } from "../../PetKey/Main/Model/1_0_Initial/Core/PathItems";
import { Published } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Core/PathItems";
import { generateUUID, Result, TransportAbstractionBuilder, TransportContext } from "@peercolab/engine";
import { TranslationAppender } from "./translationMocks";
import { applyStateTransition, transitionState } from "../../../utils/GeneralHelpers";

export function mockBaseCommandsInterceptors(builder: TransportAbstractionBuilder, appender: TranslationAppender) {
  const dbCommands: Command[] = [];
  const dbCommandsPublished: Command[] = [];

  const getCommand = (ctx: string | TransportContext): Result<Command> => {
    const id = typeof ctx === 'string' ? ctx : ctx.getPathParameter<string>('commandId');
    const command = dbCommands.find(c => c.id === id);
    if (!command) {
      return Result.notFound('Backoffice.Commands.NotFound', `Command with id ${id} not found`);
    }
    return Result.ok(command);
  };

  const saveCommand = (result: Result<Command>, ctx: TransportContext): Result<Command> => {
    if (!result.success) {
      return result;
    }
    let command = result.value;
    const existingIndex = dbCommands.findIndex(c => c.id === command.id);
    if (existingIndex !== -1) {
      const existingPublishedIndex = dbCommandsPublished.findIndex(c => c.id === command.id);
      dbCommands[existingIndex] = applyStateTransition(dbCommandsPublished, existingPublishedIndex, result.meta, command, command.state, (item, newState) => {
        item.state = newState;
        return item;
      });
    } else {
      command.translationKey = appender('commands', command.name);
      command.descriptionTranslationKey = appender('commands', command.name + 'Description', command.description);
      dbCommands.push(command);
    }
    return Result.ok(command);
  };

  return builder
    .intercept(new GetCommand().handle(async (input, ctx) => {
      return getCommand(ctx);
    }))
    .intercept(new SearchCommand().handle(async (input: SearchInput, ctx) => {
      const filtered = dbCommands
        .slice(input.offset, input.offset + input.limit)
        .filter(c =>
          c.name.toLowerCase().includes(input.query.toLowerCase()) ||
          c.description.toLowerCase().includes(input.query.toLowerCase())
        );
      return Result.ok(filtered);
    }))
    .intercept(new CreateCommand().handle(async (input: CreateCommandInput, ctx) => {
      const newCommand: Command = {
        id: input.commandId ?? generateUUID(),
        name: input.name,
        translationKey: '',
        description: input.description ?? '',
        descriptionTranslationKey: '',
        state: { state: 'draft' } as Published
      };
      return saveCommand(Result.ok(newCommand), ctx);
    }))
    .intercept(new DeleteCommand().handle(async (input, ctx) => {
      const id = ctx.getPathParameter<string>('commandId');
      const idx = dbCommands.findIndex(c => c.id === id);
      if (idx === -1) {
        return Result.notFound('Backoffice.Commands.NotFound', `Command with id ${id} not found`);
      }
      dbCommands.splice(idx, 1);
      return Result.ok(undefined);
    }))
    .intercept(new UpdateCommandName().handle(async (input: UpdateCommandNameInput, ctx) => {
      return saveCommand(getCommand(ctx)
        .maybe((value, meta) => {
          value.name = input.name;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCommandDescription().handle(async (input: UpdateCommandDescriptionInput, ctx) => {
      return saveCommand(getCommand(ctx)
        .maybe((value, meta) => {
          value.description = input.description;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCommandState().handle(async (input, ctx) => {
      return saveCommand(getCommand(ctx)
        .maybe((value, meta) => {
          value.state = transitionState(meta, value.state, input.stateTransition);
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
}
